import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Badge,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import LinkIcon from '@mui/icons-material/Link';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { EconomicInvoice, CostCategoryItem, Trailer } from '../../types';

type FilterStatus = 'ALL' | 'SUGGESTED' | 'CONFIRMED' | 'UNMATCHED' | 'IGNORED';

export default function EconomicPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  // Filter & date state
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('ALL');
  const [syncFrom, setSyncFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [syncTo, setSyncTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Detail dialog state
  const [selectedInvoice, setSelectedInvoice] = useState<EconomicInvoice | null>(null);
  const [linkData, setLinkData] = useState<{
    trailerId?: string;
    routeId?: string;
    cargoId?: string;
    costCategoryKey?: string;
  }>({});

  // Queries
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['economic-invoices', syncFrom, syncTo],
    queryFn: () => api.getEconomicInvoices({ from: syncFrom, to: syncTo }),
  });

  const { data: journalEntries = [], isLoading: entriesLoading } = useQuery({
    queryKey: ['economic-journal-entries'],
    queryFn: () => api.getEconomicJournalEntries(),
    enabled: activeTab === 1,
  });

  const { data: trailers = [] } = useQuery({
    queryKey: ['trailers'],
    queryFn: () => api.getTrailers(),
  });

  const { data: costCategories = [] } = useQuery({
    queryKey: ['cost-categories'],
    queryFn: () => api.getCostCategories(),
  });

  // Mutations
  const syncMutation = useMutation({
    mutationFn: () => api.syncEconomicInvoices(syncFrom, syncTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-invoices'] });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (id: string) => api.confirmEconomicMatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['economic-suggested-count'] });
      setSelectedInvoice(null);
    },
  });

  const linkMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof linkData }) => api.linkEconomicInvoice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['economic-suggested-count'] });
      setSelectedInvoice(null);
      setLinkData({});
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: string) => api.ignoreEconomicInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['economic-suggested-count'] });
      setSelectedInvoice(null);
    },
  });

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    if (filterStatus === 'ALL') return invoices;
    return invoices.filter((inv) => inv.matchStatus === filterStatus);
  }, [invoices, filterStatus]);

  // Count by status
  const statusCounts = useMemo(() => {
    const counts = { ALL: invoices.length, SUGGESTED: 0, CONFIRMED: 0, UNMATCHED: 0, IGNORED: 0 };
    for (const inv of invoices) {
      if (inv.matchStatus in counts) {
        counts[inv.matchStatus as keyof typeof counts]++;
      }
    }
    return counts;
  }, [invoices]);

  const handleOpenDetail = (invoice: EconomicInvoice) => {
    setSelectedInvoice(invoice);
    setLinkData({
      trailerId: invoice.trailerId || undefined,
      routeId: invoice.routeId || undefined,
      cargoId: invoice.cargoId || undefined,
      costCategoryKey: invoice.costCategoryKey || undefined,
    });
  };

  const handleSaveLink = () => {
    if (!selectedInvoice) return;
    linkMutation.mutate({ id: selectedInvoice.id, data: linkData });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency || 'DKK',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusChip = (status: EconomicInvoice['matchStatus']) => {
    switch (status) {
      case 'CONFIRMED':
        return <Chip icon={<CheckCircleIcon />} label="Tilknyttet" color="success" size="small" />;
      case 'SUGGESTED':
        return <Chip icon={<LinkIcon />} label="Forslag" color="warning" size="small" />;
      case 'IGNORED':
        return <Chip icon={<BlockIcon />} label="Ignoreret" size="small" />;
      case 'UNMATCHED':
      default:
        return <Chip icon={<HelpOutlineIcon />} label="Ikke tilknyttet" size="small" variant="outlined" />;
    }
  };

  const getEntryStatusChip = (status: string) => {
    switch (status) {
      case 'POSTED':
        return <Chip icon={<CheckCircleIcon />} label="Bogført" color="success" size="small" />;
      case 'FAILED':
        return <Chip icon={<ErrorIcon />} label="Fejlet" color="error" size="small" />;
      case 'DRAFT':
      default:
        return <Chip icon={<HourglassEmptyIcon />} label="Kladde" color="default" size="small" />;
    }
  };

  const getLinkedText = (invoice: EconomicInvoice) => {
    const parts: string[] = [];
    if (invoice.trailerId) {
      const trailer = trailers.find((t) => t.id === invoice.trailerId);
      parts.push(trailer ? trailer.number : 'Trailer');
    }
    if (invoice.routeId) parts.push(`Rute`);
    if (invoice.cargoId) parts.push(`Ordre`);
    if (invoice.costCategoryKey) {
      const cat = costCategories.find((c: CostCategoryItem) => c.key === invoice.costCategoryKey);
      parts.push(cat ? cat.name : invoice.costCategoryKey);
    }
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Bogføring</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Leverandørfakturaer" />
          <Tab label="Pushede posteringer" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          {/* Date range + sync button */}
          <Box display="flex" gap={1.5} alignItems="center" mb={2} flexWrap="wrap">
            <TextField
              type="date"
              label="Fra"
              value={syncFrom}
              onChange={(e) => setSyncFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              type="date"
              label="Til"
              value={syncTo}
              onChange={(e) => setSyncTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <Button
              variant="contained"
              startIcon={syncMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              size="small"
            >
              {syncMutation.isPending ? 'Synkroniserer...' : 'Synkroniser fra e-conomic'}
            </Button>
          </Box>

          {syncMutation.isSuccess && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              Synkronisering fuldført: {syncMutation.data.synced} fakturaer synkroniseret af {syncMutation.data.total} total.
            </Alert>
          )}

          {syncMutation.isError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              Synkronisering fejlede. Tjek at e-conomic token er konfigureret under Indstillinger.
            </Alert>
          )}

          {/* Filter chips */}
          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
            {([
              { key: 'ALL', label: 'Alle' },
              { key: 'SUGGESTED', label: 'Forslag' },
              { key: 'CONFIRMED', label: 'Tilknyttet' },
              { key: 'UNMATCHED', label: 'Ikke tilknyttet' },
              { key: 'IGNORED', label: 'Ignoreret' },
            ] as const).map(({ key, label }) => (
              <Badge
                key={key}
                badgeContent={key === 'SUGGESTED' ? statusCounts.SUGGESTED : undefined}
                color="warning"
                invisible={key !== 'SUGGESTED' || statusCounts.SUGGESTED === 0}
              >
                <Chip
                  label={`${label} (${statusCounts[key]})`}
                  variant={filterStatus === key ? 'filled' : 'outlined'}
                  color={filterStatus === key ? 'primary' : 'default'}
                  onClick={() => setFilterStatus(key)}
                />
              </Badge>
            ))}
          </Box>

          {/* Invoice table */}
          {invoicesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : filteredInvoices.length === 0 ? (
            <Alert severity="info">
              Ingen fakturaer fundet. Synkroniser fra e-conomic for at importere fakturaer.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dato</TableCell>
                    <TableCell>Leverandør</TableCell>
                    <TableCell>Fakturanr.</TableCell>
                    <TableCell align="right">Beløb</TableCell>
                    <TableCell>Tekst</TableCell>
                    <TableCell>Tilknyttet til</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenDetail(invoice)}
                    >
                      <TableCell>{new Date(invoice.date).toLocaleDateString('da-DK')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {invoice.supplierName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{invoice.supplierNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{invoice.invoiceNumber || '-'}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatAmount(invoice.amount, invoice.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {invoice.text || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getLinkedText(invoice)}</TableCell>
                      <TableCell>{getStatusChip(invoice.matchStatus)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {entriesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : journalEntries.length === 0 ? (
            <Alert severity="info">
              Ingen posteringer endnu. Push omkostninger til e-conomic fra omkostningssiden.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dato</TableCell>
                    <TableCell>Beskrivelse</TableCell>
                    <TableCell align="right">Beløb</TableCell>
                    <TableCell>Journal</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {journalEntries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{new Date(entry.createdAt).toLocaleDateString('da-DK')}</TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('da-DK', {
                          style: 'currency',
                          currency: 'DKK',
                          minimumFractionDigits: 2,
                        }).format(entry.amount)}
                      </TableCell>
                      <TableCell>#{entry.journalNumber}</TableCell>
                      <TableCell>
                        {getEntryStatusChip(entry.status)}
                        {entry.errorMessage && (
                          <Typography variant="caption" color="error" display="block">
                            {entry.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Invoice Detail Dialog */}
      <Dialog
        open={!!selectedInvoice}
        onClose={() => { setSelectedInvoice(null); setLinkData({}); }}
        maxWidth="sm"
        fullWidth
      >
        {selectedInvoice && (
          <>
            <DialogTitle>
              Fakturadetaljer
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} pt={1}>
                {/* Invoice info */}
                <Box>
                  <Typography variant="body2" color="text.secondary">Leverandør</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedInvoice.supplierName} (#{selectedInvoice.supplierNumber})
                  </Typography>
                </Box>
                <Box display="flex" gap={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Dato</Typography>
                    <Typography variant="body1">{new Date(selectedInvoice.date).toLocaleDateString('da-DK')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Beløb</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatAmount(selectedInvoice.amount, selectedInvoice.currency)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Fakturanr.</Typography>
                    <Typography variant="body1">{selectedInvoice.invoiceNumber || '-'}</Typography>
                  </Box>
                </Box>
                {selectedInvoice.text && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Tekst</Typography>
                    <Typography variant="body1">{selectedInvoice.text}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Box mt={0.5}>{getStatusChip(selectedInvoice.matchStatus)}</Box>
                </Box>

                {/* Suggestion section */}
                {selectedInvoice.matchStatus === 'SUGGESTED' && selectedInvoice.suggestedTrailerId && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Systemet foreslår en match:
                    </Typography>
                    <Typography variant="body2">
                      Trailer: {trailers.find((t: Trailer) => t.id === selectedInvoice.suggestedTrailerId)?.number || selectedInvoice.suggestedTrailerId}
                    </Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => confirmMutation.mutate(selectedInvoice.id)}
                        disabled={confirmMutation.isPending}
                      >
                        Godkend
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => ignoreMutation.mutate(selectedInvoice.id)}
                        disabled={ignoreMutation.isPending}
                      >
                        Afvis
                      </Button>
                    </Box>
                  </Alert>
                )}

                {/* Manual linking */}
                <Box mt={1}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Manuel tilknytning
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    <Autocomplete
                      size="small"
                      options={trailers}
                      getOptionLabel={(option: Trailer) => `${option.number}${option.alias ? ` (${option.alias})` : ''}`}
                      value={trailers.find((t: Trailer) => t.id === linkData.trailerId) || null}
                      onChange={(_, value) => setLinkData((prev) => ({ ...prev, trailerId: value?.id }))}
                      renderInput={(params) => <TextField {...params} label="Trailer" />}
                    />
                    <TextField
                      size="small"
                      label="Rute-ID"
                      value={linkData.routeId || ''}
                      onChange={(e) => setLinkData((prev) => ({ ...prev, routeId: e.target.value || undefined }))}
                    />
                    <TextField
                      size="small"
                      label="Ordre-ID"
                      value={linkData.cargoId || ''}
                      onChange={(e) => setLinkData((prev) => ({ ...prev, cargoId: e.target.value || undefined }))}
                    />
                    <FormControl size="small">
                      <InputLabel>Kategori</InputLabel>
                      <Select
                        value={linkData.costCategoryKey || ''}
                        label="Kategori"
                        onChange={(e) => setLinkData((prev) => ({ ...prev, costCategoryKey: e.target.value || undefined }))}
                      >
                        <MenuItem value="">
                          <em>Ingen</em>
                        </MenuItem>
                        {costCategories.map((cat: CostCategoryItem) => (
                          <MenuItem key={cat.key} value={cat.key}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color }} />
                              {cat.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => ignoreMutation.mutate(selectedInvoice.id)}
                disabled={ignoreMutation.isPending || selectedInvoice.matchStatus === 'IGNORED'}
              >
                Ignorer
              </Button>
              <Box flex={1} />
              <Button onClick={() => { setSelectedInvoice(null); setLinkData({}); }}>
                Annuller
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveLink}
                disabled={linkMutation.isPending || (!linkData.trailerId && !linkData.routeId && !linkData.cargoId && !linkData.costCategoryKey)}
              >
                {linkMutation.isPending ? 'Gemmer...' : 'Gem'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
